import { Info } from "lucide-react";
import { cn } from "../cn";
import { Tooltip } from "./Tooltip";
import type { AssuranceLevel } from "@/domain/types";

// The assurance ladder (design brief §5.4): fixed order high to low, four distinct
// colours from the --assure-* tokens so Statement-matched (teal) and Document-backed
// (signal blue) never read as the same blue. Verified uses the success green.
export const ASSURE_ORDER: AssuranceLevel[] = ["verified", "statement_matched", "document_backed", "self_reported"];

const META: Record<AssuranceLevel, { label: string; dot: string; fill: string; help: string }> = {
  verified: {
    label: "Verified",
    dot: "bg-assureVerified",
    fill: "bg-assureVerified",
    help: "Independently verified by oversight through sampling or a site visit.",
  },
  statement_matched: {
    label: "Statement-matched",
    dot: "bg-assureMatched",
    fill: "bg-assureMatched",
    help: "Reconciled against a bank or mobile money statement.",
  },
  document_backed: {
    label: "Document-backed",
    dot: "bg-assureDocumented",
    fill: "bg-assureDocumented",
    help: "Backed by a receipt, invoice or site photo attached to the spend.",
  },
  self_reported: {
    label: "Self-reported",
    dot: "bg-assureSelf",
    fill: "bg-assureSelf",
    help: "Entered by the spender; no document, statement match or independent check yet.",
  },
};

/** An outline assurance chip with a ladder-coloured dot (design brief §5.4). */
export function AssuranceBadge({ level, className }: { level: AssuranceLevel; className?: string }) {
  const m = META[level];
  return (
    <Tooltip label={m.help}>
      <span
        className={cn(
          "inline-flex h-[22px] items-center gap-1.5 whitespace-nowrap rounded-full border border-line bg-card px-2 text-[11px] font-medium leading-none text-mute",
          className,
        )}
      >
        <span className={cn("h-1.5 w-1.5 rounded-full", m.dot)} aria-hidden />
        {m.label}
      </span>
    </Tooltip>
  );
}

export function assuranceLabel(level: AssuranceLevel): string {
  return META[level].label;
}

/**
 * The stacked coverage bar: segments in ladder order, a vizTrack remainder, and a
 * legend of dot + label + mono amount. Used on Controls Assurance, programme analytics
 * and the funder justification panel.
 */
export function StackedAssuranceBar({
  counts,
  formatAmount,
  className,
}: {
  counts: Record<AssuranceLevel, number>;
  /** Render legend amounts (e.g. money). Defaults to the raw number. */
  formatAmount?: (n: number) => string;
  className?: string;
}) {
  const total = ASSURE_ORDER.reduce((a, l) => a + (counts[l] || 0), 0) || 1;
  const fmt = (n: number) => (formatAmount ? formatAmount(n) : String(n));
  return (
    <div className={className}>
      <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-vizTrack">
        {ASSURE_ORDER.map((l) => {
          const pct = ((counts[l] || 0) / total) * 100;
          if (pct <= 0) return null;
          return <div key={l} className={cn("h-full", META[l].fill)} style={{ width: `${pct}%` }} title={`${META[l].label}: ${fmt(counts[l] || 0)}`} />;
        })}
      </div>
      <ul className="mt-3 grid grid-cols-2 gap-x-6 gap-y-1.5 sm:grid-cols-4">
        {ASSURE_ORDER.map((l) => (
          <li key={l} className="flex items-center gap-1.5 text-xs text-mute">
            <span className={cn("h-2 w-2 shrink-0 rounded-full", META[l].dot)} aria-hidden />
            <span className="flex-1 truncate">{META[l].label}</span>
            <span className="data text-ink">{fmt(counts[l] || 0)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Info affordance with a tooltip explaining the four ladder levels (recognition over recall). */
export function LadderExplainer({ className }: { className?: string }) {
  return (
    <Tooltip
      contentClassName="w-72 px-3 py-2.5"
      label={
        <div className="space-y-2">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-white/60">The assurance ladder</p>
          <ul className="space-y-2">
            {ASSURE_ORDER.map((l) => (
              <li key={l} className="flex gap-2">
                <span className={cn("mt-1 h-2 w-2 shrink-0 rounded-full", META[l].dot)} aria-hidden />
                <span className="min-w-0">
                  <span className="font-semibold text-white">{META[l].label}</span>
                  <span className="mt-0.5 block font-normal leading-snug text-white/70">{META[l].help}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      }
    >
      <button
        type="button"
        className={cn("focus-ring inline-flex items-center rounded-full text-faint hover:text-mute", className)}
        aria-label="What the assurance levels mean"
      >
        <Info className="h-3.5 w-3.5" />
      </button>
    </Tooltip>
  );
}
